"use client";

import React, { useId } from "react";
import {
  type ViewToggleOption,
  isGenreLabel,
  prepareViewToggleOptions
} from "./view-toggle.shared";
import styles from "./view-toggle.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * ViewToggle — VT1-VT7. A native `<input type="radio">` group, which is what gives VT7 for free:
 * Tab reaches the group once, on whichever option is selected; the arrow keys move between options
 * and select as they move, because that is what a native radio group already does; and the browser
 * announces each option's own label and which one is checked, with no ARIA authored here at all.
 */

export interface ViewToggleProps {
  /** VT1. Two entries in the content team's authored order (today Signature
   *  Packages first); fewer than two and the toggle does not render at all
   *  — VT2 still decides the view, just with no control to show for it. */
  options?: ViewToggleOption[];
  /** The organism root's current view (plan §1.2). Always one of
   *  `options[].value`, resolved server-side by VT2 before this mounts. */
  value: string;
  /** Reports the newly chosen option's value. The root is what reacts to it
   *  — this leaf never changes what is on screen itself (VT3). */
  onChange: (value: string) => void;
  /** The name the radios share, which makes them one group. */
  paramName?: string;
  /** Accessible name for the group — content-managed; VT7 requires one. */
  ariaLabel?: string;
  className?: string;
}

const DEFAULT_PARAM_NAME = "view";

function ViewToggleImpl({
  options,
  value,
  onChange,
  paramName = DEFAULT_PARAM_NAME,
  ariaLabel = "",
  className
}: ViewToggleProps) {
  const { renderable: safeOptions, malformed, duplicates } = prepareViewToggleOptions(options);
  const groupName = useId();

  if (process.env.NODE_ENV !== "production") {
    if (ariaLabel.trim().length === 0) {
      console.error(
        "[ViewToggle] `ariaLabel` is blank, so the group has no accessible " +
          "name and a screen reader announces the options without ever " +
          "saying what is being chosen (VT7)."
      );
    }
    if (malformed > 0) {
      console.warn(
        `[ViewToggle] dropped ${malformed} malformed option${malformed === 1 ? "" : "s"}. ` +
          "Every option needs a non-blank `value` and `label`."
      );
    }
    for (const duplicate of duplicates) {
      console.warn(`[ViewToggle] dropped a duplicate option value "${duplicate}".`);
    }
    if (safeOptions.length > 0 && !safeOptions.some(option => option.value === value)) {
      console.error(
        `[ViewToggle] controlled value "${value}" matches no option, so no ` +
          "option is selected and the toggle looks broken. Keep `value` in " +
          "step with `options`."
      );
    }
  }

  /* VT1 — the content team can switch the toggle off; the view is still
     chosen by VT2, just with no control on screen for it. */
  if (safeOptions.length < 2) {
    return null;
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    /* VT3 — choosing the option already selected does nothing; a native
       radio group never fires `change` for the option already checked, so
       there is nothing to guard here. */
    onChange(event.target.value);
  };

  return (
    <form className={[styles.form, className].filter(Boolean).join(" ")}>
      <fieldset className={styles.fieldset}>
        {ariaLabel.trim() !== "" && <legend className="dds-sr-only">{ariaLabel.trim()}</legend>}
        <span className={styles.track}>
          {safeOptions.map((option, index) => {
            const id = `${groupName}-opt${index}`;
            return (
              <span className={styles.slot} key={option.value}>
                <input
                  checked={option.value === value}
                  className={styles.input}
                  id={id}
                  name={paramName}
                  onChange={handleChange}
                  type="radio"
                  value={option.value}
                />
                <label className={styles.label} htmlFor={id}>
                  <span className={styles.text}>{option.label}</span>
                </label>
              </span>
            );
          })}
        </span>
      </fieldset>
    </form>
  );
}
export const ViewToggle = withSafeRender(ViewToggleImpl, "ViewToggle");

/** VT2 step 1 ("?view=genre … today the option's label must contain
 *  'genre'"), exposed so the root can decide what an incoming request asked
 *  for against ITS own authored options before this component ever mounts. */
export { isGenreLabel };
export type { ViewToggleOption };

export default ViewToggle;
